import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ChangeTable } from './ChangeTable';
import { Commentary } from './Commentary';

import type { AlertColor } from '@mui/material/Alert';
import type { Changes } from '@platform/schema/changes';

/** One thing a reviewer can do about a rewrite. `primary` is the recommended one. */
export interface ChangeAction {
  label: string;
  colour: 'primary' | 'error';
  onClick: () => void;
}

/**
 * A rewrite, old beside new, and what can be done about it — GEN-56, SKILL-03, GEN-76.
 *
 * Extracted when the editor's applied findings needed the same view. Two screens showing
 * "here is what changed, undo it" should not drift into two layouts, and the copy that
 * did drift lost the case that matters most: a rewrite that changed nothing reads as a
 * failure unless it says so in words, and an empty table does not.
 *
 * What each caller keeps is its own sentence and its own verbs. A list, rather than the
 * single undo it started as: GEN-76 turned an applied finding into a question with two
 * answers, and a second prop beside `action` would have been the same button twice.
 */
export function ChangeSummary({
  changes,
  unchanged,
  severity = 'success',
  lead,
  note,
  actions = [],
  disabled,
}: {
  changes: Changes;
  /** What to say when the rewrite came back identical. */
  unchanged: string;
  severity?: AlertColor;
  /** The sentence above the table. The count is appended by the caller if it wants one. */
  lead: string;
  /**
   * An agent's account of what it did, between the sentence and the table, and which
   * agent wrote it. One prop rather than two, so a note can never be attributed to
   * whoever the last caller happened to name.
   */
  note?: { author: string; text: string };
  /** What can be done about it, in reading order, or nothing to show the diff alone. */
  actions?: readonly ChangeAction[];
  disabled?: boolean;
}) {
  if (changes.changes.length === 0)
    return <Alert severity="info">{unchanged}</Alert>;

  return (
    <Stack spacing={1}>
      <Alert severity={severity}>{lead}</Alert>

      {note !== undefined && (
        <Commentary author={note.author}>
          <Typography variant="body2" color="text.secondary">
            {note.text}
          </Typography>
        </Commentary>
      )}

      <ChangeTable changes={changes.changes} />

      {actions.length > 0 && (
        <Stack direction="row" spacing={1}>
          {actions.map((one) => (
            <Button
              key={one.label}
              size="small"
              variant={one.colour === 'primary' ? 'contained' : 'text'}
              color={one.colour}
              disabled={disabled}
              onClick={one.onClick}
            >
              {one.label}
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** "3 fields" — the count both callers put in their sentence, pluralised once. */
export const fieldCount = (changes: Changes): string =>
  `${String(changes.changes.length)} field${changes.changes.length === 1 ? '' : 's'}`;
